import React, { useEffect, useState } from 'react';
import { Plus, Grid3x3, Table as TableIcon, Loader, Lock, Trash2, Mail, Phone, Users, X, Save, Bell, BellOff, Clock } from 'lucide-react';
import { useAlert } from '../../../../components/AlertProvider';
import { useConfirm } from '../../../../components/ConfirmDialog';
import adminClientMembersService from '../../../../services/adminClientMembersService';
import api, { apiService } from '../../../../services/api';
import { ClientMember, ClientMemberCreate, ClientMemberUpdate, MemberRole, MEMBER_ROLE_LABELS } from '../../../../types/client';
import { MemberRoleBadge } from '@/components/clients';
import { PhoneInput } from '@/components/ui/PhoneInput';
import { Client, SalesPoint } from './types';

interface ContactRoleOption {
  value: string;
  label: string;
}

interface ContactsTabProps {
  client: Client;
  contacts: ClientMember[];
  clientId: number;
  onReload: () => Promise<void>;
  onReloadClient: () => Promise<void>;
}

export const ContactsTab: React.FC<ContactsTabProps> = ({ client, contacts, clientId, onReload, onReloadClient }) => {
  const { showError, showSuccess } = useAlert();
  const { showConfirm } = useConfirm();

  const [resettingPassword, setResettingPassword] = useState<number | 'main' | null>(null);
  const [togglingNotif, setTogglingNotif] = useState<number | 'main' | null>(null);
  const [viewMode, setViewMode] = useState<'card' | 'table' | 'list'>('table');
  const [contactRoles, setContactRoles] = useState<ContactRoleOption[]>([]);

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [selectedContact, setSelectedContact] = useState<ClientMember | null>(null);
  const [saving, setSaving] = useState(false);

  // Main contact edit modal
  const [isMainModalOpen, setIsMainModalOpen] = useState(false);
  const [mainSaving, setMainSaving] = useState(false);
  const [mainForm, setMainForm] = useState<{
    first_name: string;
    last_name: string;
    email: string;
    phone: string;
    password: string;
    is_active: boolean;
    member_type: MemberRole;
    access_all_sales_points: boolean;
    can_create_orders: boolean;
    can_manage_invoices: boolean;
    can_create_sales_points: boolean;
    receive_notifications: boolean;
  }>({
    first_name: client.first_name || '',
    last_name: client.last_name || '',
    email: client.email || '',
    phone: client.phone || '',
    password: '',
    is_active: client.is_active,
    member_type: (client.member_type || 'gerant') as MemberRole,
    access_all_sales_points: client.access_all_sales_points ?? true,
    can_create_orders: client.can_create_orders ?? true,
    can_manage_invoices: client.can_manage_invoices ?? true,
    can_create_sales_points: client.can_create_sales_points ?? true,
    receive_notifications: client.receive_notifications ?? true,
  });

  // Sync mainForm when client prop changes
  useEffect(() => {
    setMainForm({
      first_name: client.first_name || '',
      last_name: client.last_name || '',
      email: client.email || '',
      phone: client.phone || '',
      password: '',
      is_active: client.is_active,
      member_type: (client.member_type || 'gerant') as MemberRole,
      access_all_sales_points: client.access_all_sales_points ?? true,
      can_create_orders: client.can_create_orders ?? true,
      can_manage_invoices: client.can_manage_invoices ?? true,
      can_create_sales_points: client.can_create_sales_points ?? true,
      receive_notifications: client.receive_notifications ?? true,
    });
  }, [client]);

  // Form state
  const [formData, setFormData] = useState<Partial<ClientMemberCreate>>({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    is_primary: false,
    member_type: 'employe',
    password: '',
    is_active: true,
    receive_notifications: true,
    access_all_sales_points: true,
    can_create_orders: true,
    can_manage_invoices: true,
    can_create_sales_points: false
  });

  const [salesPoints, setSalesPoints] = useState<SalesPoint[]>([]);
  const [salesPointsLoading, setSalesPointsLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const loadSalesPoints = async (): Promise<void> => {
      setSalesPointsLoading(true);
      try {
        const res = await api.get('/clients/sales-points', { params: { client_id: clientId } });
        const raw = res.data;
        const list: SalesPoint[] = Array.isArray(raw) ? raw : (Array.isArray(raw?.items) ? raw.items : []);
        if (!cancelled) setSalesPoints(list);
      } catch (e) {
        if (!cancelled) setSalesPoints([]);
      } finally {
        if (!cancelled) setSalesPointsLoading(false);
      }
    };

    const loadContactRoles = async (): Promise<void> => {
      try {
        const settings = await apiService.getGroupSettings();
        if (!cancelled) setContactRoles(settings.contact_roles || []);
      } catch (e) {
        console.error('Failed to load contact roles', e);
      }
    };

    void loadSalesPoints();
    void loadContactRoles();

    return () => {
      cancelled = true;
    };
  }, [clientId]);

  const handleOpenCreate = () => {
    setModalMode('create');
    setSelectedContact(null);
    setFormData({
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      is_primary: false,
      member_type: 'employe',
      password: '',
      is_active: false,
      receive_notifications: false,
      access_all_sales_points: false,
      sales_point_ids: [],
      can_create_orders: false,
      can_manage_invoices: false,
      can_create_sales_points: false,
      send_welcome_email: false
    });
    setIsModalOpen(true);
  };

  const handleOpenEdit = (contact: ClientMember) => {
    setModalMode('edit');
    setSelectedContact(contact);

    const isGerant = contact.member_type === 'gerant';

    setFormData({
      first_name: contact.first_name,
      last_name: contact.last_name,
      email: contact.email,
      phone: contact.phone || '',
      is_primary: !!contact.is_primary,
      member_type: contact.member_type,
      password: '', // Leave empty if not changing
      is_active: isGerant ? true : contact.is_active,
      receive_notifications: contact.receive_notifications ?? true,
      access_all_sales_points: isGerant ? true : contact.access_all_sales_points,
      sales_point_ids: isGerant ? [] : (contact.access_all_sales_points ? [] : (contact.sales_point_ids ?? [])),
      can_create_orders: isGerant ? true : contact.can_create_orders,
      can_manage_invoices: isGerant ? true : contact.can_manage_invoices,
      can_create_sales_points: isGerant ? true : contact.can_create_sales_points,
      send_welcome_email: false
    });
    setIsModalOpen(true);
  };

  const toggleSalesPointSelection = (salesPointId: number) => {
    setFormData((prev) => {
      const current = Array.isArray(prev.sales_point_ids) ? prev.sales_point_ids : [];
      const next = current.includes(salesPointId)
        ? current.filter((id) => id !== salesPointId)
        : [...current, salesPointId];
      return { ...prev, sales_point_ids: next };
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payloadBase: Partial<ClientMemberCreate> = { ...formData };
      const isGerant = payloadBase.member_type === 'gerant';
      if (isGerant) {
        payloadBase.is_active = true;
        payloadBase.access_all_sales_points = true;
        delete payloadBase.sales_point_ids;
        payloadBase.can_create_sales_points = true;
        payloadBase.can_create_orders = true;
        payloadBase.can_manage_invoices = true;
      } else if (payloadBase.access_all_sales_points) {
        delete payloadBase.sales_point_ids;
      } else {
        payloadBase.sales_point_ids = Array.isArray(payloadBase.sales_point_ids) ? payloadBase.sales_point_ids : [];
      }

      if (modalMode === 'create') {
        // Password is optional for simple contacts (non-principal contacts without app access)
        await adminClientMembersService.create(clientId, {
          ...payloadBase,
          client_id: clientId
        } as ClientMemberCreate);
        showSuccess('Contact créé avec succès');
      } else if (selectedContact) {
        const updateData: ClientMemberUpdate = { ...payloadBase };
        if (!updateData.password) delete updateData.password;

        await adminClientMembersService.update(clientId, selectedContact.id, updateData);
        showSuccess('Contact mis à jour avec succès');
      }
      setIsModalOpen(false);
      await onReload();
      if (payloadBase.is_primary) {
        await onReloadClient();
      }
    } catch (error: any) {
      console.error('Error saving contact:', error);
      showError(error.response?.data?.detail || 'Erreur lors de l\'enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const handleOpenMainEdit = () => {
    setMainForm({
      first_name: client.first_name || '',
      last_name: client.last_name || '',
      email: client.email || '',
      phone: client.phone || '',
      password: '',
      is_active: client.is_active,
      member_type: (client.member_type || 'gerant') as MemberRole,
      access_all_sales_points: true,
      can_create_orders: client.can_create_orders ?? true,
      can_manage_invoices: client.can_manage_invoices ?? true,
      can_create_sales_points: client.can_create_sales_points ?? true,
      receive_notifications: client.receive_notifications ?? true,
    });
    setIsMainModalOpen(true);
  };

  const handleSubmitMain = async (e: React.FormEvent) => {
    e.preventDefault();
    setMainSaving(true);
    try {
      const payload: Record<string, any> = {
        first_name: mainForm.first_name,
        last_name: mainForm.last_name,
        email: mainForm.email,
        phone: mainForm.phone,
        is_active: mainForm.is_active,
        receive_notifications: mainForm.receive_notifications,
      };

      const isGerant = mainForm.member_type === 'gerant';
      payload.member_type = mainForm.member_type;
      payload.access_all_sales_points = true;
      payload.can_create_sales_points = isGerant ? true : mainForm.can_create_sales_points;
      payload.can_create_orders = isGerant ? true : mainForm.can_create_orders;
      payload.can_manage_invoices = isGerant ? true : mainForm.can_manage_invoices;

      if (mainForm.password) {
        payload.password = mainForm.password;
      }

      await api.patch(`/clients/${clientId}`, payload);
      showSuccess('Contact principal mis à jour avec succès');
      setIsMainModalOpen(false);
      await onReloadClient();
    } catch (error: any) {
      console.error('Error saving main contact:', error);
      showError(error.response?.data?.detail || 'Erreur lors de la mise à jour du contact principal');
    } finally {
      setMainSaving(false);
    }
  };

  const deleteContact = async (contact: ClientMember) => {
    const confirmed = await showConfirm({
      title: 'Supprimer le contact',
      message: `Êtes-vous sûr de vouloir supprimer ${contact.first_name} ${contact.last_name} ?`,
      confirmText: 'Supprimer',
      cancelText: 'Annuler',
    });
    if (!confirmed) return;

    try {
      await adminClientMembersService.delete(clientId, contact.id);
      showSuccess('Contact supprimé');
      await onReload();
      await onReloadClient();
    } catch (error: any) {
      showError('Erreur lors de la suppression du contact');
    }
  };

  const resetPassword = async (contactId: number | 'main', name: string) => {
    const confirmed = await showConfirm({
      title: 'Réinitialiser le mot de passe',
      message: `Voulez-vous envoyer un email de réinitialisation de mot de passe à ${name} ?`,
      confirmText: 'Envoyer',
      cancelText: 'Annuler',
    });
    if (!confirmed) return;

    setResettingPassword(contactId);
    try {
      if (contactId === 'main') {
        await api.post(`/clients/${clientId}/send-reset-password`);
        showSuccess(`Email de réinitialisation envoyé à ${name}`);
      } else {
        await api.post(`/clients/${clientId}/contacts/${contactId}/send-reset-password`);
        showSuccess(`Email de réinitialisation envoyé à ${name}`);
      }
    } catch (error: any) {
      const detail = error.response?.data?.detail;
      showError(typeof detail === 'string' ? detail : 'Erreur lors de l\'envoi de l\'email de réinitialisation');
    } finally {
      setResettingPassword(null);
    }
  };

  const toggleNotification = async (contactId: number | 'main', currentStatus: boolean, name: string) => {
    setTogglingNotif(contactId);
    try {
      if (contactId === 'main') {
        await api.patch(`/clients/${clientId}`, { receive_notifications: !currentStatus });
        showSuccess(`Notifications ${!currentStatus ? 'activées' : 'désactivées'} pour ${name}`);
        await onReloadClient();
      } else {
        await adminClientMembersService.update(clientId, contactId as number, { receive_notifications: !currentStatus });
        showSuccess(`Notifications ${!currentStatus ? 'activées' : 'désactivées'} pour ${name}`);
        await onReload();
        await onReloadClient();
      }
    } catch (error: any) {
      showError('Erreur lors de la modification des notifications');
    } finally {
      setTogglingNotif(null);
    }
  };

  return (
    <div className="bg-white dark:bg-slate-900 rounded-lg shadow-sm border border-gray-200 dark:border-slate-700">
      <div className="p-3 sm:p-6">
        <div className="flex flex-col gap-3 sm:gap-4 md:flex-row md:items-center md:justify-between mb-4 sm:mb-6">
          <h2 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-slate-100">Contacts</h2>
          <div className="flex items-center gap-2 sm:gap-3">
            <button
              onClick={handleOpenCreate}
              className="inline-flex items-center px-3 sm:px-4 py-1.5 sm:py-2 text-sm bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 transition-colors"
            >
              <Plus className="w-4 h-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Ajouter un contact</span>
              <span className="sm:hidden">Ajouter</span>
            </button>
            <div className="hidden sm:flex items-center gap-1 border border-slate-300 dark:border-slate-600 rounded-lg p-1">
              <button
                type="button"
                onClick={() => setViewMode('card')}
                className={`p-2 rounded flex items-center justify-center ${viewMode === 'card' ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-400' : 'text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'}`}
                title="Affichage cartes"
                aria-label="Affichage cartes"
              >
                <Grid3x3 className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={() => setViewMode('table')}
                className={`p-2 rounded flex items-center justify-center ${viewMode === 'table' ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-400' : 'text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800'}`}
                title="Affichage tableau"
                aria-label="Affichage tableau"
              >
                <TableIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>

        {viewMode === 'table' && typeof window !== 'undefined' && window.innerWidth >= 640 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-slate-700">
              <thead className="bg-gray-50 dark:bg-slate-800">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wider">
                    Nom
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wider">
                    Téléphone
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wider">
                    Rôle
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wider">
                    Statut
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wider">
                    Dernière connexion
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-slate-400 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-slate-900 divide-y divide-gray-200 dark:divide-slate-700">
                {contacts.length === 0 ? (
                  <tr
                    onClick={handleOpenMainEdit}
                    className="bg-emerald-50 dark:bg-emerald-900/10 hover:bg-emerald-100 dark:hover:bg-emerald-900/20 transition-colors cursor-pointer"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="font-medium text-gray-900 dark:text-slate-100">
                          {`${client.first_name ?? ''} ${client.last_name ?? ''}`.trim() || client.company_name}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-slate-400">
                      {client.email}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-slate-400">
                      {client.phone || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <MemberRoleBadge role={(client.member_type || 'gerant') as MemberRole} />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {client.is_active ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">
                          Actif
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300">
                          Inactif
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-slate-400">
                      {client.last_login
                        ? new Date(client.last_login).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' }) + ' ' + new Date(client.last_login).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
                        : <span className="italic text-slate-400">Jamais connecté</span>
                      }
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleNotification('main', !!client.receive_notifications, `${client.first_name ?? ''} ${client.last_name ?? ''}`.trim() || client.company_name);
                          }}
                          disabled={togglingNotif === 'main'}
                          className={`p-2 rounded-lg transition-colors disabled:opacity-50 ${client.receive_notifications
                            ? 'text-emerald-600 dark:text-emerald-400 hover:bg-emerald-100 dark:hover:bg-emerald-900/20'
                            : 'text-gray-400 dark:text-slate-500 hover:bg-gray-100 dark:hover:bg-slate-800'
                            }`}
                          title={client.receive_notifications ? "Désactiver les notifications" : "Activer les notifications"}
                        >
                          {togglingNotif === 'main' ? (
                            <Loader className="h-4 w-4 animate-spin" />
                          ) : client.receive_notifications ? (
                            <Bell className="h-4 w-4" />
                          ) : (
                            <BellOff className="h-4 w-4" />
                          )}
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); resetPassword('main', `${client.first_name ?? ''} ${client.last_name ?? ''}`.trim() || client.company_name); }}
                          disabled={resettingPassword === 'main'}
                          className="p-2 text-orange-600 dark:text-orange-400 hover:bg-orange-100 dark:hover:bg-orange-900/20 rounded-lg transition-colors disabled:opacity-50"
                          title="Réinitialiser le mot de passe"
                        >
                          {resettingPassword === 'main' ? (
                            <Loader className="h-4 w-4 animate-spin" />
                          ) : (
                            <Lock className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                ) : null}

                {/* Other contacts */}
                {contacts.map((contact) => (
                  <tr
                    key={contact.id}
                    onClick={() => handleOpenEdit(contact)}
                    className="hover:bg-gray-50 dark:hover:bg-slate-800 transition-colors cursor-pointer"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="font-medium text-gray-900 dark:text-slate-100">
                          {contact.first_name} {contact.last_name}
                        </div>
                        {contact.is_primary ? (
                          <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-600 text-white">
                            Principal
                          </span>
                        ) : null}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-slate-400">
                      {contact.email}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-slate-400">
                      {contact.phone || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <MemberRoleBadge role={contact.member_type} />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {contact.is_active ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300">
                          Actif
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300">
                          Inactif
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-slate-400">
                      {(() => {
                        const loginDate = contact.last_login || (contact.is_primary ? client.last_login : null);
                        return loginDate
                          ? new Date(loginDate).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' }) + ' ' + new Date(loginDate).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
                          : <span className="italic text-slate-400">Jamais connecté</span>;
                      })()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleNotification(contact.id, !!contact.receive_notifications, `${contact.first_name} ${contact.last_name}`);
                          }}
                          disabled={togglingNotif === contact.id}
                          className={`p-2 rounded-lg transition-colors disabled:opacity-50 ${contact.receive_notifications
                            ? 'text-emerald-600 dark:text-emerald-400 hover:bg-emerald-100 dark:hover:bg-emerald-900/20'
                            : 'text-gray-400 dark:text-slate-500 hover:bg-gray-100 dark:hover:bg-slate-800'
                            }`}
                          title={contact.receive_notifications ? "Désactiver les notifications" : "Activer les notifications"}
                        >
                          {togglingNotif === contact.id ? (
                            <Loader className="h-4 w-4 animate-spin" />
                          ) : contact.receive_notifications ? (
                            <Bell className="h-4 w-4" />
                          ) : (
                            <BellOff className="h-4 w-4" />
                          )}
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); resetPassword(contact.id, `${contact.first_name} ${contact.last_name}`); }}
                          disabled={resettingPassword === contact.id}
                          className="p-2 text-orange-600 dark:text-orange-400 hover:bg-orange-100 dark:hover:bg-orange-900/20 rounded-lg transition-colors disabled:opacity-50"
                          title="Réinitialiser le mot de passe"
                        >
                          {resettingPassword === contact.id ? (
                            <Loader className="h-4 w-4 animate-spin" />
                          ) : (
                            <Lock className="h-4 w-4" />
                          )}
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); deleteContact(contact); }}
                          className="p-2 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                          title="Supprimer"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-4">
            {contacts.length === 0 ? (
              <div
                onClick={handleOpenMainEdit}
                className="bg-white dark:bg-slate-900 rounded-lg sm:rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm p-3 sm:p-4 flex flex-col cursor-pointer hover:border-emerald-500 transition-colors"
              >
                <div className="flex items-start justify-between gap-2 sm:gap-3 mb-2 sm:mb-3">
                  <div>
                    <div className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                      {`${client.first_name ?? ''} ${client.last_name ?? ''}`.trim() || client.company_name}
                    </div>
                  </div>
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${client.is_active ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                      }`}
                  >
                    {client.is_active ? 'Actif' : 'Inactif'}
                  </span>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-slate-50 dark:bg-slate-800 flex items-center justify-center shrink-0">
                      <Mail className="w-4 h-4 text-emerald-500" />
                    </div>
                    <span className="text-slate-600 dark:text-slate-400 break-all">{client.email}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-slate-50 dark:bg-slate-800 flex items-center justify-center shrink-0">
                      <Phone
                        className={`w-4 h-4 ${client.phone ? 'text-emerald-500' : 'text-red-500'}`}
                      />
                    </div>
                    <span className={`text-slate-600 dark:text-slate-400 ${client.phone ? '' : 'italic text-slate-400 dark:text-slate-500'}`}>
                      {client.phone || 'Non renseigné'}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-slate-50 dark:bg-slate-800 flex items-center justify-center shrink-0">
                      <Users className="w-4 h-4 text-emerald-500" />
                    </div>
                    <MemberRoleBadge role={(client.member_type || 'gerant') as MemberRole} />
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-slate-50 dark:bg-slate-800 flex items-center justify-center shrink-0">
                      <Clock className="w-4 h-4 text-blue-500" />
                    </div>
                    <span className="text-slate-600 dark:text-slate-400">
                      {client.last_login
                        ? new Date(client.last_login).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' }) + ' ' + new Date(client.last_login).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
                        : <span className="italic text-slate-400 dark:text-slate-500">Jamais connecté</span>
                      }
                    </span>
                  </div>
                </div>
                <div className="mt-4 flex justify-end">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleNotification('main', !!client.receive_notifications, `${client.first_name ?? ''} ${client.last_name ?? ''}`.trim() || client.company_name);
                      }}
                      disabled={togglingNotif === 'main'}
                      className={`inline-flex items-center gap-2 px-3 py-1.5 text-xs rounded-lg transition-colors disabled:opacity-50 ${client.receive_notifications
                        ? 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100 dark:bg-emerald-900/20 dark:text-emerald-400 dark:hover:bg-emerald-900/30'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-slate-800 dark:text-slate-400 dark:hover:bg-slate-700'
                        }`}
                    >
                      {togglingNotif === 'main' ? (
                        <Loader className="h-4 w-4 animate-spin" />
                      ) : client.receive_notifications ? (
                        <Bell className="h-4 w-4" />
                      ) : (
                        <BellOff className="h-4 w-4" />
                      )}
                      <span>Notif.</span>
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); resetPassword('main', `${client.first_name ?? ''} ${client.last_name ?? ''}`.trim() || client.company_name); }}
                      disabled={resettingPassword === 'main'}
                      className="inline-flex items-center gap-2 px-3 py-1.5 text-xs rounded-lg bg-orange-50 text-orange-700 hover:bg-orange-100 dark:bg-orange-900/20 dark:text-orange-400 dark:hover:bg-orange-900/30 transition-colors disabled:opacity-50"
                    >
                      {resettingPassword === 'main' ? (
                        <Loader className="h-4 w-4 animate-spin" />
                      ) : (
                        <Lock className="h-4 w-4" />
                      )}
                      <span>Réinitialiser</span>
                    </button>
                  </div>
                </div>
              </div>
            ) : null}

            {/* Other contacts cards */}
            {contacts.map((contact) => (
              <div
                key={contact.id}
                onClick={() => handleOpenEdit(contact)}
                className="bg-white dark:bg-slate-900 rounded-lg sm:rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm p-3 sm:p-4 flex flex-col cursor-pointer hover:border-emerald-500 transition-colors"
              >
                <div className="flex items-start justify-between gap-2 sm:gap-3 mb-2 sm:mb-3">
                  <div>
                    <div className="flex items-center">
                      <div className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                        {contact.first_name} {contact.last_name}
                      </div>
                      {contact.is_primary ? (
                        <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-600 text-white">
                          Principal
                        </span>
                      ) : null}
                    </div>
                    <div className="mt-1 text-xs text-slate-500 dark:text-slate-400 break-all">{contact.email}</div>
                  </div>
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${contact.is_active ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300' : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                      }`}
                  >
                    {contact.is_active ? 'Actif' : 'Inactif'}
                  </span>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-slate-50 dark:bg-slate-800 flex items-center justify-center shrink-0">
                      <Mail
                        className={`w-4 h-4 ${contact.email ? 'text-emerald-500' : 'text-red-500'}`}
                      />
                    </div>
                    <span
                      className={`text-slate-600 dark:text-slate-400 break-all ${contact.email ? '' : 'italic text-slate-400 dark:text-slate-500'
                        }`}
                    >
                      {contact.email || 'Non renseigné'}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-slate-50 dark:bg-slate-800 flex items-center justify-center shrink-0">
                      <Phone
                        className={`w-4 h-4 ${contact.phone ? 'text-emerald-500' : 'text-red-500'}`}
                      />
                    </div>
                    <span
                      className={`text-slate-600 dark:text-slate-400 ${contact.phone ? '' : 'italic text-slate-400 dark:text-slate-500'
                        }`}
                    >
                      {contact.phone || 'Non renseigné'}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-slate-50 dark:bg-slate-800 flex items-center justify-center shrink-0">
                      <Users className="w-4 h-4 text-emerald-500" />
                    </div>
                    <MemberRoleBadge role={contact.member_type} />
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-lg bg-slate-50 dark:bg-slate-800 flex items-center justify-center shrink-0">
                      <Clock className="w-4 h-4 text-blue-500" />
                    </div>
                    <span className="text-slate-600 dark:text-slate-400">
                      {(() => {
                        const loginDate = contact.last_login || (contact.is_primary ? client.last_login : null);
                        return loginDate
                          ? new Date(loginDate).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' }) + ' ' + new Date(loginDate).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
                          : <span className="italic text-slate-400 dark:text-slate-500">Jamais connecté</span>;
                      })()}
                    </span>
                  </div>
                </div>
                <div className="mt-4 flex justify-end gap-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleNotification(contact.id, !!contact.receive_notifications, `${contact.first_name} ${contact.last_name}`);
                    }}
                    disabled={togglingNotif === contact.id}
                    className={`p-1.5 rounded-lg transition-colors disabled:opacity-50 ${contact.receive_notifications
                      ? 'text-emerald-600 dark:text-emerald-400 hover:bg-emerald-100 dark:hover:bg-emerald-900/20'
                      : 'text-gray-400 dark:text-slate-500 hover:bg-gray-100 dark:hover:bg-slate-800'
                      }`}
                    title={contact.receive_notifications ? "Désactiver les notifications" : "Activer les notifications"}
                  >
                    {togglingNotif === contact.id ? (
                      <Loader className="h-4 w-4 animate-spin" />
                    ) : contact.receive_notifications ? (
                      <Bell className="h-4 w-4" />
                    ) : (
                      <BellOff className="h-4 w-4" />
                    )}
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); resetPassword(contact.id, `${contact.first_name} ${contact.last_name}`); }}
                    disabled={resettingPassword === contact.id}
                    className="p-1.5 text-orange-600 dark:text-orange-400 hover:bg-orange-100 dark:hover:bg-orange-900/20 rounded-lg transition-colors disabled:opacity-50"
                    title="Réinitialiser le mot de passe"
                  >
                    {resettingPassword === contact.id ? (
                      <Loader className="h-4 w-4 animate-spin" />
                    ) : (
                      <Lock className="h-4 w-4" />
                    )}
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); deleteContact(contact); }}
                    className="p-1.5 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                    title="Supprimer"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal Création/Edition Contact */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" onClick={() => setIsModalOpen(false)}>
              <div className="absolute inset-0 bg-gray-500 dark:bg-slate-900 opacity-75"></div>
            </div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>

            <div className="inline-block align-bottom bg-white dark:bg-slate-800 rounded-xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full max-h-[90vh] overflow-y-auto">
              <form onSubmit={handleSubmit}>
                <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200 dark:border-slate-700 flex items-center justify-between">
                  <h3 className="text-base sm:text-lg font-bold text-gray-900 dark:text-slate-100">
                    {modalMode === 'create' ? 'Ajouter un contact' : 'Modifier le contact'}
                  </h3>
                  <button
                    type="button"
                    onClick={() => setIsModalOpen(false)}
                    className="text-gray-400 hover:text-gray-500 dark:hover:text-slate-300 transition-colors"
                    title="Fermer"
                    aria-label="Fermer"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>

                <div className="px-4 sm:px-6 py-4 sm:py-6 space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="member_first_name" className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                        Prénom
                      </label>
                      <input
                        id="member_first_name"
                        type="text"
                        required
                        value={formData.first_name}
                        onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-emerald-500 bg-white dark:bg-slate-900 text-gray-900 dark:text-slate-100"
                      />
                    </div>
                    <div>
                      <label htmlFor="member_last_name" className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                        Nom
                      </label>
                      <input
                        id="member_last_name"
                        type="text"
                        value={formData.last_name}
                        onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-emerald-500 bg-white dark:bg-slate-900 text-gray-900 dark:text-slate-100"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="member_email" className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                        Email <span className="text-xs text-gray-500 dark:text-slate-400">(Optionnel)</span>
                      </label>
                      <input
                        id="member_email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => {
                          const newEmail = e.target.value;
                          setFormData({
                            ...formData,
                            email: newEmail,
                            send_welcome_email: newEmail ? formData.send_welcome_email : false
                          });
                        }}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-emerald-500 bg-white dark:bg-slate-900 text-gray-900 dark:text-slate-100"
                      />
                    </div>
                    <div>
                      <label htmlFor="member_phone" className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                        Téléphone
                      </label>
                      <PhoneInput
                        id="member_phone"
                        value={formData.phone || ''}
                        onChange={(val) => setFormData({ ...formData, phone: val })}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="member_role" className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                        Rôle
                      </label>
                      <select
                        id="member_role"
                        title="Rôle"
                        value={formData.member_type}
                        onChange={(e) => {
                          const nextRole = e.target.value as MemberRole;
                          if (nextRole === 'gerant') {
                            setFormData({
                              ...formData,
                              member_type: nextRole,
                              is_active: true,
                              access_all_sales_points: true,
                              can_create_sales_points: true,
                              can_create_orders: true,
                              can_manage_invoices: true,
                            });
                          } else {
                            setFormData({ ...formData, member_type: nextRole });
                          }
                        }}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-emerald-500 bg-white dark:bg-slate-900 text-gray-900 dark:text-slate-100"
                      >
                        {contactRoles.length > 0 ? (
                          contactRoles.map((role) => (
                            <option key={role.value} value={role.value}>{role.label}</option>
                          ))
                        ) : (
                          Object.entries(MEMBER_ROLE_LABELS).map(([value, label]) => (
                            <option key={value} value={value}>{label}</option>
                          ))
                        )}
                      </select>
                    </div>
                    {modalMode === 'edit' && (
                      <div>
                        <label htmlFor="member_password" className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                          Mot de passe (laisser vide pour ne pas changer)
                        </label>
                        <input
                          id="member_password"
                          type="password"
                          value={formData.password}
                          onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-emerald-500 bg-white dark:bg-slate-900 text-gray-900 dark:text-slate-100"
                        />
                      </div>
                    )}
                  </div>

                  <div className="space-y-3 pt-2">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="send_welcome_email"
                        checked={!!formData.send_welcome_email}
                        onChange={(e) => setFormData({ ...formData, send_welcome_email: e.target.checked })}
                        disabled={!formData.email}
                        className="h-4 w-4 text-emerald-600 focus:ring-emerald-500 border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                      />
                      <label htmlFor="send_welcome_email" className={`ml-2 block text-sm ${!formData.email ? 'text-gray-400 dark:text-slate-500' : 'text-gray-700 dark:text-slate-300'}`}>
                        <Mail className="inline-block w-4 h-4 mr-1" />
                        Envoyer l'email de bienvenue
                      </label>
                    </div>
                    {formData.send_welcome_email && (
                      <div className="ml-6 p-2 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                        <p className="text-xs text-blue-700 dark:text-blue-300">
                          Un email de vérification sera envoyé au contact pour qu'il définisse son mot de passe.
                        </p>
                      </div>
                    )}
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="is_primary"
                        checked={!!formData.is_primary}
                        onChange={(e) => setFormData({ ...formData, is_primary: e.target.checked })}
                        className="h-4 w-4 text-emerald-600 focus:ring-emerald-500 border-gray-300 rounded"
                      />
                      <label htmlFor="is_primary" className="ml-2 block text-sm text-gray-700 dark:text-slate-300">
                        Contact principal
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="receive_notifications"
                        checked={!!formData.receive_notifications}
                        onChange={(e) => setFormData({ ...formData, receive_notifications: e.target.checked })}
                        className="h-4 w-4 text-emerald-600 focus:ring-emerald-500 border-gray-300 rounded"
                      />
                      <label htmlFor="receive_notifications" className="ml-2 block text-sm text-gray-700 dark:text-slate-300">
                        Notification mail
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="is_active"
                        checked={formData.member_type === 'gerant' ? true : !!formData.is_active}
                        onClick={(e) => {
                          if (formData.member_type === 'gerant') e.preventDefault();
                        }}
                        onChange={(e) => {
                          if (formData.member_type === 'gerant') return;
                          setFormData({ ...formData, is_active: e.target.checked });
                        }}
                        className="h-4 w-4 text-emerald-600 focus:ring-emerald-500 border-gray-300 rounded"
                      />
                      <label htmlFor="is_active" className="ml-2 block text-sm text-gray-700 dark:text-slate-300">
                        Compte actif
                      </label>
                    </div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="access_all_sales_points"
                        checked={formData.member_type === 'gerant' ? true : !!formData.access_all_sales_points}
                        onClick={(e) => {
                          if (formData.member_type === 'gerant') e.preventDefault();
                        }}
                        onChange={(e) => {
                          if (formData.member_type === 'gerant') return;
                          const nextChecked = e.target.checked;
                          setFormData((prev) => ({
                            ...prev,
                            access_all_sales_points: nextChecked,
                            sales_point_ids: nextChecked ? [] : (Array.isArray(prev.sales_point_ids) ? prev.sales_point_ids : []),
                          }));
                        }}
                        className="h-4 w-4 text-emerald-600 focus:ring-emerald-500 border-gray-300 rounded"
                      />
                      <label htmlFor="access_all_sales_points" className="ml-2 block text-sm text-gray-700 dark:text-slate-300">
                        Accès à tous les points de vente
                      </label>
                    </div>

                    {formData.member_type !== 'gerant' && !formData.access_all_sales_points && (
                      <div className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700 dark:text-slate-300">
                          Points de vente autorisés
                        </label>
                        {salesPointsLoading ? (
                          <div className="text-sm text-slate-500 dark:text-slate-400">Chargement...</div>
                        ) : salesPoints.length === 0 ? (
                          <div className="text-sm text-slate-500 dark:text-slate-400">Aucun point de vente disponible.</div>
                        ) : (
                          <div className="max-h-48 overflow-y-auto border border-gray-200 dark:border-slate-700 rounded-lg p-3 space-y-2 bg-white dark:bg-slate-900">
                            {salesPoints.map((sp) => {
                              const checked = Array.isArray(formData.sales_point_ids)
                                ? formData.sales_point_ids.includes(sp.id)
                                : false;
                              return (
                                <label key={sp.id} className="flex items-start gap-2 text-sm text-gray-700 dark:text-slate-300">
                                  <input
                                    type="checkbox"
                                    className="mt-1 h-4 w-4 text-emerald-600 focus:ring-emerald-500 border-gray-300 rounded"
                                    checked={checked}
                                    onChange={() => toggleSalesPointSelection(sp.id)}
                                  />
                                  <span className="leading-5">
                                    <span className="font-medium">{sp.name}</span>
                                    {(sp.address || sp.city || sp.country) && (
                                      <span className="block text-xs text-slate-500 dark:text-slate-400">
                                        {[sp.address, sp.city, sp.country].filter(Boolean).join(' • ')}
                                      </span>
                                    )}
                                  </span>
                                </label>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="can_create_orders"
                          checked={formData.member_type === 'gerant' ? true : !!formData.can_create_orders}
                          onClick={(e) => {
                            if (formData.member_type === 'gerant') e.preventDefault();
                          }}
                          onChange={(e) => {
                            if (formData.member_type === 'gerant') return;
                            setFormData({ ...formData, can_create_orders: e.target.checked });
                          }}
                          className="h-4 w-4 text-emerald-600 focus:ring-emerald-500 border-gray-300 rounded"
                        />
                        <label htmlFor="can_create_orders" className="ml-2 block text-sm text-gray-700 dark:text-slate-300">
                          Peut passer des commandes
                        </label>
                      </div>
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="can_manage_invoices"
                          checked={formData.member_type === 'gerant' ? true : !!formData.can_manage_invoices}
                          onClick={(e) => {
                            if (formData.member_type === 'gerant') e.preventDefault();
                          }}
                          onChange={(e) => {
                            if (formData.member_type === 'gerant') return;
                            setFormData({ ...formData, can_manage_invoices: e.target.checked });
                          }}
                          className="h-4 w-4 text-emerald-600 focus:ring-emerald-500 border-gray-300 rounded"
                        />
                        <label htmlFor="can_manage_invoices" className="ml-2 block text-sm text-gray-700 dark:text-slate-300">
                          Peut voir les factures
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="px-4 sm:px-6 py-3 sm:py-4 bg-gray-50 dark:bg-slate-800/50 border-t border-gray-200 dark:border-slate-700 flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => setIsModalOpen(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className="inline-flex items-center px-4 py-2 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50"
                  >
                    {saving ? (
                      <Loader className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Save className="w-4 h-4 mr-2" />
                    )}
                    {modalMode === 'create' ? 'Créer le contact' : 'Enregistrer'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Modal Modification Contact Principal */}
      {isMainModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" onClick={() => setIsMainModalOpen(false)}>
              <div className="absolute inset-0 bg-gray-500 dark:bg-slate-900 opacity-75"></div>
            </div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>

            <div className="inline-block align-bottom bg-white dark:bg-slate-800 rounded-xl text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full max-h-[90vh] overflow-y-auto">
              <form onSubmit={handleSubmitMain}>
                <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200 dark:border-slate-700 flex items-center justify-between">
                  <h3 className="text-base sm:text-lg font-bold text-gray-900 dark:text-slate-100">Modifier le contact</h3>
                  <button
                    type="button"
                    onClick={() => setIsMainModalOpen(false)}
                    className="text-gray-400 hover:text-gray-500 dark:hover:text-slate-300 transition-colors"
                    title="Fermer"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>

                <div className="px-4 sm:px-6 py-4 sm:py-6 space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="main_company_name" className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">Société</label>
                      <input
                        id="main_company_name"
                        type="text"
                        disabled
                        value={client.company_name}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-gray-50 dark:bg-slate-900 text-gray-900 dark:text-slate-100 opacity-75"
                      />
                    </div>

                    <div>
                      <label htmlFor="main_first_name" className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">Prénom</label>
                      <input
                        id="main_first_name"
                        type="text"
                        value={mainForm.first_name}
                        onChange={(e) => setMainForm({ ...mainForm, first_name: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-emerald-500 bg-white dark:bg-slate-900 text-gray-900 dark:text-slate-100"
                      />
                    </div>
                    <div>
                      <label htmlFor="main_last_name" className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">Nom</label>
                      <input
                        id="main_last_name"
                        type="text"
                        value={mainForm.last_name}
                        onChange={(e) => setMainForm({ ...mainForm, last_name: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-emerald-500 bg-white dark:bg-slate-900 text-gray-900 dark:text-slate-100"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="main_email" className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">Email</label>
                      <input
                        id="main_email"
                        type="email"
                        required
                        value={mainForm.email}
                        onChange={(e) => setMainForm({ ...mainForm, email: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-emerald-500 bg-white dark:bg-slate-900 text-gray-900 dark:text-slate-100"
                      />
                    </div>

                    <div>
                      <label htmlFor="main_phone" className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">Téléphone</label>
                      <PhoneInput
                        id="main_phone"
                        value={mainForm.phone}
                        onChange={(val) => setMainForm({ ...mainForm, phone: val })}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="main_role" className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">Rôle</label>
                      <select
                        id="main_role"
                        title="Rôle"
                        value={mainForm.member_type}
                        onChange={(e) => setMainForm({ ...mainForm, member_type: e.target.value as MemberRole })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-emerald-500 bg-white dark:bg-slate-900 text-gray-900 dark:text-slate-100"
                      >
                        {contactRoles.length > 0 ? (
                          contactRoles.map((role) => (
                            <option key={role.value} value={role.value}>{role.label}</option>
                          ))
                        ) : (
                          Object.entries(MEMBER_ROLE_LABELS).map(([value, label]) => (
                            <option key={value} value={value}>{label}</option>
                          ))
                        )}
                      </select>
                    </div>

                    <div>
                      <label htmlFor="main_password" className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                        Mot de passe (laisser vide pour ne pas changer)
                      </label>
                      <input
                        id="main_password"
                        name="main_new_password"
                        type="password"
                        autoComplete="new-password"
                        value={mainForm.password}
                        onChange={(e) => setMainForm({ ...mainForm, password: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-emerald-500 bg-white dark:bg-slate-900 text-gray-900 dark:text-slate-100"
                      />
                    </div>
                  </div>

                  <div className="space-y-3 pt-2">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="main_is_active"
                        checked={mainForm.is_active}
                        onChange={(e) => setMainForm({ ...mainForm, is_active: e.target.checked })}
                        className="h-4 w-4 text-emerald-600 focus:ring-emerald-500 border-gray-300 rounded"
                      />
                      <label htmlFor="main_is_active" className="ml-2 block text-sm text-gray-700 dark:text-slate-300">
                        Compte actif
                      </label>
                    </div>

                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="main_receive_notifications"
                        checked={mainForm.receive_notifications}
                        onChange={(e) => setMainForm({ ...mainForm, receive_notifications: e.target.checked })}
                        className="h-4 w-4 text-emerald-600 focus:ring-emerald-500 border-gray-300 rounded"
                      />
                      <label htmlFor="main_receive_notifications" className="ml-2 block text-sm text-gray-700 dark:text-slate-300">
                        Notification mail
                      </label>
                    </div>

                    {mainForm.member_type !== 'gerant' && (
                      <>
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            id="main_access_all_sales_points"
                            checked={true}
                            disabled
                            className="h-4 w-4 text-emerald-600 focus:ring-emerald-500 border-gray-300 rounded"
                          />
                          <label htmlFor="main_access_all_sales_points" className="ml-2 block text-sm text-gray-700 dark:text-slate-300">
                            Accès à tous les points de vente
                          </label>
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div className="flex items-center">
                            <input
                              type="checkbox"
                              id="main_can_create_orders"
                              checked={mainForm.can_create_orders}
                              onChange={(e) => setMainForm({ ...mainForm, can_create_orders: e.target.checked })}
                              className="h-4 w-4 text-emerald-600 focus:ring-emerald-500 border-gray-300 rounded"
                            />
                            <label htmlFor="main_can_create_orders" className="ml-2 block text-sm text-gray-700 dark:text-slate-300">
                              Peut passer des commandes
                            </label>
                          </div>
                          <div className="flex items-center">
                            <input
                              type="checkbox"
                              id="main_can_manage_invoices"
                              checked={mainForm.can_manage_invoices}
                              onChange={(e) => setMainForm({ ...mainForm, can_manage_invoices: e.target.checked })}
                              className="h-4 w-4 text-emerald-600 focus:ring-emerald-500 border-gray-300 rounded"
                            />
                            <label htmlFor="main_can_manage_invoices" className="ml-2 block text-sm text-gray-700 dark:text-slate-300">
                              Peut voir les factures
                            </label>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>

                <div className="px-4 sm:px-6 py-3 sm:py-4 bg-gray-50 dark:bg-slate-800/50 border-t border-gray-200 dark:border-slate-700 flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={() => setIsMainModalOpen(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    disabled={mainSaving}
                    className="inline-flex items-center px-4 py-2 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700 transition-colors disabled:opacity-50"
                  >
                    {mainSaving ? (
                      <Loader className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Save className="w-4 h-4 mr-2" />
                    )}
                    Enregistrer
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
